import { FC, MouseEvent, useRef } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { FormattedMessage } from "react-intl";

import { FormWrapper } from "@framework/mui-form";
import { TextInput } from "@framework/mui-inputs-core";
import { useAsu } from "@framework/provider-asu";

import { FilterAutocompleteInput } from "../../common/inputs/filter-autocomplete";
import { TrainTypeInput } from "../../common/inputs/train-type";
import { FilterButtons } from "./buttons";
import { FormReset } from "./form-reset";

export interface IFilterDialogProps {
  open: boolean;
  onConfirm: (values?: any, form?: any) => Promise<void>;
  onClose: () => void;
}

export const FilterDialog: FC<IFilterDialogProps> = props => {
  const { onClose, onConfirm, open } = props;

  const { filters, getAllNestedTrains } = useAsu();
  const innerRef = useRef<HTMLFormElement | null>(null) as any;

  const handleSubmit = async (e: MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    if (innerRef && innerRef.current) {
      innerRef.current.dispatchEvent(new Event("submit", { bubbles: false, cancelable: true }));
    }

    return Promise.resolve();
  };

  const handleClose = (event: any, reason: string) => {
    if (reason !== "backdropClick") {
      onClose();
    }
  };

  return (
    <FormWrapper
      onSubmit={onConfirm}
      initialValues={filters}
      innerRef={innerRef}
      showButtons={false}
      showPrompt={false}
    >
      <Dialog fullWidth maxWidth="md" onClose={handleClose} open={open}>
        <DialogTitle id="reports-dialog-title" sx={{ fontWeight: "700" }}>
          <FormattedMessage id="dialogs.filters" />
        </DialogTitle>
        <DialogContent id="reports-dialog-content">
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ display: "flex" }}>
              <FilterAutocompleteInput
                name="path"
                variant="outlined"
                controller="stations"
                isGrouped
                groupName={option => option.title.charAt(0)}
                filterOption={option => Object.keys(getAllNestedTrains(~~option.id)).length !== 0}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
              <TextInput variant="outlined" name="trainName" />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
              <TextInput variant="outlined" name="trainSerial" />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: "flex" }}>
              <TextInput variant="outlined" name="trainPosition" />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: "flex" }}>
              <TextInput variant="outlined" name="instanceNumber" />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: "flex" }}>
              <TextInput variant="outlined" name="rfid" />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
              <TrainTypeInput />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
              <FilterAutocompleteInput
                name="technologicalProcess"
                variant="outlined"
                controller="technological-process"
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
              <FilterAutocompleteInput name="manufacturer" variant="outlined" controller="manufacturer" />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
              <FilterAutocompleteInput name="equipmentGroup" variant="outlined" controller="equipment-group" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <FilterButtons handleSubmit={handleSubmit} onClose={onClose} />
        </DialogActions>
      </Dialog>
      <FormReset />
    </FormWrapper>
  );
};
